// Exercise — Interactive exercise renderer for Anna's show_exercise tool
//
// Routes to sub-renderers: multiple_choice (radio buttons + feedback),
// explain_in_own_words (textarea → chat), fill_in_blank (input + check).
// comparison_table shows a fallback card (deferred to v2).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ExerciseProps {
  type: 'fill_in_blank' | 'multiple_choice' | 'comparison_table' | 'explain_in_own_words';
  question: string;
  options?: string[];
  correctAnswer?: string;
  hint?: string;
  accentColor: string;
  onSubmitAnswer?: (answer: string) => void;
}

// ---------- Sub-components ----------

function MultipleChoice({
  question,
  options,
  correctAnswer,
  hint,
}: {
  question: string;
  options: string[];
  correctAnswer?: string;
  hint?: string;
}) {
  const { t } = useTranslation('session');
  const [selected, setSelected] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const handleSelect = (option: string) => {
    if (showResult) return;
    setSelected(option);
    setShowResult(true);
  };

  const isCorrect = selected === correctAnswer;

  return (
    <div>
      <p className="text-sm font-medium text-gray-200 mb-3 whitespace-pre-wrap">
        {question}
      </p>
      <div className="space-y-2">
        {options.map((option, i) => {
          let optionStyle = 'border-gray-700 hover:border-gray-500';
          if (showResult && option === selected) {
            optionStyle = isCorrect
              ? 'border-emerald-500 bg-emerald-500/10'
              : 'border-red-500 bg-red-500/10';
          }
          if (showResult && option === correctAnswer && !isCorrect) {
            optionStyle = 'border-emerald-500/50 bg-emerald-500/5';
          }
          return (
            <button
              key={i}
              onClick={() => handleSelect(option)}
              disabled={showResult}
              className={`w-full text-left px-3 py-2 rounded-lg border text-sm text-gray-300 transition-colors ${optionStyle} disabled:cursor-default`}
            >
              {option}
            </button>
          );
        })}
      </div>
      {showResult && (
        <p className={`mt-3 text-sm font-medium animate-fade-in-simple ${isCorrect ? 'text-emerald-400' : 'text-red-400'}`}>
          {isCorrect ? t('exercise.correct') : `${t('exercise.incorrect')} ${correctAnswer}`}
        </p>
      )}
      {hint && !showHint && !isCorrect && (
        <button
          onClick={() => setShowHint(true)}
          className="mt-2 text-xs text-gray-500 hover:text-gray-300 underline"
        >
          {t('exercise.hint')}
        </button>
      )}
      {showHint && (
        <p className="mt-2 text-xs text-gray-400 italic animate-fade-in-simple">
          {hint}
        </p>
      )}
    </div>
  );
}

function ExplainInOwnWords({
  question,
  hint,
  onSubmitAnswer,
}: {
  question: string;
  hint?: string;
  onSubmitAnswer?: (answer: string) => void;
}) {
  const { t } = useTranslation('session');
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const handleSubmit = () => {
    if (!answer.trim() || !onSubmitAnswer) return;
    onSubmitAnswer(`Mit svar på opgaven: ${answer.trim()}`);
    setSubmitted(true);
  };

  return (
    <div>
      <p className="text-sm font-medium text-gray-200 mb-3 whitespace-pre-wrap">
        {question}
      </p>
      <textarea
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        disabled={submitted}
        placeholder={t('exercise.explain.placeholder')}
        className="w-full h-24 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 resize-none focus:outline-none focus:border-gray-500 disabled:opacity-60"
      />
      <div className="flex items-center gap-3 mt-2">
        <button
          onClick={handleSubmit}
          disabled={!answer.trim() || submitted}
          className="px-4 py-1.5 rounded-lg text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white transition-colors disabled:opacity-40 disabled:cursor-default"
        >
          {t('exercise.submit')}
        </button>
        {hint && !showHint && !submitted && (
          <button
            onClick={() => setShowHint(true)}
            className="text-xs text-gray-500 hover:text-gray-300 underline"
          >
            {t('exercise.hint')}
          </button>
        )}
      </div>
      {showHint && (
        <p className="mt-2 text-xs text-gray-400 italic animate-fade-in-simple">
          {hint}
        </p>
      )}
    </div>
  );
}

function FillInBlank({
  question,
  correctAnswer,
  hint,
}: {
  question: string;
  correctAnswer?: string;
  hint?: string;
}) {
  const { t } = useTranslation('session');
  const [answer, setAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const handleSubmit = () => {
    if (!answer.trim()) return;
    setShowResult(true);
  };

  const isCorrect = correctAnswer
    ? answer.trim().toLowerCase() === correctAnswer.trim().toLowerCase()
    : false;

  // Split question on ___ to create inline input
  const parts = question.split('___');

  return (
    <div>
      <div className="text-sm text-gray-200 mb-3 flex flex-wrap items-center gap-1">
        {parts.map((part, i) => (
          <span key={i}>
            <span className="whitespace-pre-wrap">{part}</span>
            {i < parts.length - 1 && (
              <input
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                disabled={showResult}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                className="inline-block w-32 mx-1 px-2 py-0.5 bg-gray-800 border border-gray-600 rounded text-sm text-gray-200 focus:outline-none focus:border-gray-400 disabled:opacity-60"
                placeholder="..."
              />
            )}
          </span>
        ))}
      </div>
      {!showResult && (
        <button
          onClick={handleSubmit}
          disabled={!answer.trim()}
          className="px-4 py-1.5 rounded-lg text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white transition-colors disabled:opacity-40 disabled:cursor-default"
        >
          {t('exercise.submit')}
        </button>
      )}
      {showResult && (
        <p className={`mt-2 text-sm font-medium animate-fade-in-simple ${isCorrect ? 'text-emerald-400' : 'text-red-400'}`}>
          {isCorrect ? t('exercise.correct') : `${t('exercise.incorrect')} ${correctAnswer}`}
        </p>
      )}
      {hint && !showHint && showResult && !isCorrect && (
        <button
          onClick={() => setShowHint(true)}
          className="mt-2 text-xs text-gray-500 hover:text-gray-300 underline"
        >
          {t('exercise.hint')}
        </button>
      )}
      {showHint && (
        <p className="mt-2 text-xs text-gray-400 italic animate-fade-in-simple">
          {hint}
        </p>
      )}
    </div>
  );
}

// ---------- Main component ----------

export default function Exercise({
  type,
  question,
  options,
  correctAnswer,
  hint,
  accentColor,
  onSubmitAnswer,
}: ExerciseProps) {
  const { t } = useTranslation('session');

  const BORDER_MAP: Record<string, string> = {
    amber: 'border-amber-500/30',
    blue: 'border-blue-500/30',
    indigo: 'border-indigo-500/30',
  };
  const border = BORDER_MAP[accentColor] || 'border-gray-700';

  return (
    <div className={`rounded-xl border ${border} bg-gray-900/50 p-4 animate-fade-in-simple`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-base">{'\u{270F}\u{FE0F}'}</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          {t('exercise.label')}
        </span>
      </div>

      {type === 'multiple_choice' && options ? (
        <MultipleChoice
          question={question}
          options={options}
          correctAnswer={correctAnswer}
          hint={hint}
        />
      ) : type === 'explain_in_own_words' ? (
        <ExplainInOwnWords
          question={question}
          hint={hint}
          onSubmitAnswer={onSubmitAnswer}
        />
      ) : type === 'fill_in_blank' ? (
        <FillInBlank
          question={question}
          correctAnswer={correctAnswer}
          hint={hint}
        />
      ) : (
        /* comparison_table fallback */
        <div className="text-sm text-gray-400">
          <p className="font-medium text-gray-200 mb-2 whitespace-pre-wrap">{question}</p>
          <p className="italic">{t('exercise.comparison.unavailable')}</p>
        </div>
      )}
    </div>
  );
}
